import React from 'react';
import { Button, Icon, Popconfirm, TimePicker, message } from 'antd';
import moment from 'moment';
import * as HMS from '../../../../common/component/Index';
import * as Func from '../../../../../../helpers/functions';
import {
    DEFAULT_TIME_ZONE_OFFSET,
    validateOperatingHour,
} from './operatingHoursUtils';

const TIME_FORMAT = 'HH:mm';
const TABLE_COLUMNS = ['startTime', 'endTime'];

const getEditableItem = () => ({
    startTime: undefined,
    endTime: undefined,
    isActive: true,
    timeZoneOffset: DEFAULT_TIME_ZONE_OFFSET,
});

class OperatingHoursTable extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            operatingHours: props.value || [],
            editingIndex: null,
            draftRow: getEditableItem(),
        };

        this.startTimeRef = React.createRef();
    }

    componentDidMount() {
        if (this.props.autoFocusOnEmpty && !(this.props.value || []).length) {
            this.handleAddRow();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.value !== this.props.value && this.props.value !== this.state.operatingHours) {
            this.setState({
                operatingHours: this.props.value || [],
            });
        }

        if (prevState.editingIndex !== this.state.editingIndex && this.state.editingIndex !== null) {
            setTimeout(() => {
                const input = this.startTimeRef.current;
                if (input && typeof input.focus === 'function') {
                    input.focus();
                }
            }, 0);
        }
    }

    updateParent = (operatingHours) => {
        this.setState({ operatingHours });
        this.props.onChange && this.props.onChange(operatingHours);
    }

    handleAddRow = () => {
        if (this.state.editingIndex !== null) {
            const isSaved = this.handleSaveRow();
            if (!isSaved) return;
        }

        const nextOperatingHours = [...this.state.operatingHours, getEditableItem()];
        this.setState({
            operatingHours: nextOperatingHours,
            editingIndex: nextOperatingHours.length - 1,
            draftRow: nextOperatingHours[nextOperatingHours.length - 1],
        });
    }

    handleEditRow = (index) => {
        if (this.state.editingIndex !== null && this.state.editingIndex !== index) {
            const isSaved = this.handleSaveRow();
            if (!isSaved) return;
        }

        this.setState({
            editingIndex: index,
            draftRow: { ...this.state.operatingHours[index] },
        });
    }

    handleCancelEdit = () => {
        const { editingIndex, operatingHours } = this.state;
        if (editingIndex === null) return;

        const currentItem = operatingHours[editingIndex];
        if (!currentItem?.startTime && !currentItem?.endTime) {
            const nextOperatingHours = operatingHours.filter((_, index) => index !== editingIndex);
            this.updateParent(nextOperatingHours);
        }

        this.setState({
            editingIndex: null,
            draftRow: getEditableItem(),
        });
    }

    handleDeleteRow = (index) => {
        const nextOperatingHours = this.state.operatingHours.filter((_, itemIndex) => itemIndex !== index);
        this.setState({
            editingIndex: this.state.editingIndex === index ? null : this.state.editingIndex,
            draftRow: getEditableItem(),
        });
        this.updateParent(nextOperatingHours);
    }

    handleDraftChange = (field, time) => {
        this.setState({
            draftRow: {
                ...this.state.draftRow,
                [field]: time ? time.format(TIME_FORMAT) : undefined,
                isActive: true,
                timeZoneOffset: this.state.draftRow?.timeZoneOffset || DEFAULT_TIME_ZONE_OFFSET,
            }
        });
    }

    handleSaveRow = () => {
        const { editingIndex, draftRow, operatingHours } = this.state;
        if (editingIndex === null) return true;

        const validation = validateOperatingHour(
            {
                ...draftRow,
                isActive: true,
                timeZoneOffset: draftRow?.timeZoneOffset || DEFAULT_TIME_ZONE_OFFSET,
            },
            operatingHours,
            editingIndex
        );

        if (!validation.valid) {
            message.error(validation.message);
            return false;
        }

        const nextOperatingHours = [...operatingHours];
        nextOperatingHours[editingIndex] = {
            ...draftRow,
            isActive: true,
            timeZoneOffset: draftRow?.timeZoneOffset || DEFAULT_TIME_ZONE_OFFSET,
        };

        this.setState({
            editingIndex: null,
            draftRow: getEditableItem(),
        });
        this.updateParent(nextOperatingHours);
        return true;
    }

    renderTimeCell = (field, value, index) => {
        const { editingIndex, draftRow } = this.state;
        const isEditing = editingIndex === index;

        if (!isEditing) {
            return value || '';
        }

        return (
            <TimePicker
                ref={field === 'startTime' ? this.startTimeRef : undefined}
                allowClear={false}
                disabled={this.props.disabled}
                format={TIME_FORMAT}
                inputReadOnly
                minuteStep={1}
                value={draftRow[field] ? moment(draftRow[field], TIME_FORMAT) : null}
                onChange={(time) => this.handleDraftChange(field, time)}
                style={{ width: '100%' }}
            />
        );
    }

    renderActionCell = (_, record, index) => {
        const { editingIndex } = this.state;
        const { disabled } = this.props;
        const isEditing = editingIndex === index;

        if (isEditing) {
            return (
                <div className="d-flex align-items-center">
                        <Button type="link" onClick={this.handleSaveRow} disabled={disabled}>
                            <Icon type="check" />
                        </Button>
                    <Button type="link" onClick={this.handleCancelEdit}>
                        <Icon type="close" />
                    </Button>
                    <Popconfirm
                        title="Bạn có chắc muốn xóa khung giờ này?"
                        okText="Xóa"
                        cancelText="Hủy"
                        onConfirm={() => this.handleDeleteRow(index)}
                    >
                        <Button type="link" disabled={disabled}>
                            <Icon type="delete" />
                        </Button>
                    </Popconfirm>
                </div>
            );
        }

        return (
            <div className="d-flex align-items-center">
                <Button type="link" onClick={() => this.handleEditRow(index)} disabled={disabled}>
                    <Icon type="form" />
                </Button>
                <Popconfirm
                    title="Bạn có chắc muốn xóa khung giờ này?"
                    okText="Xóa"
                    cancelText="Hủy"
                    onConfirm={() => this.handleDeleteRow(index)}
                >
                    <Button type="link" disabled={disabled}>
                        <Icon type="delete" />
                    </Button>
                </Popconfirm>
            </div>
        );
    }

    render() {
        const { disabled } = this.props;
        const titleItemTable = Func.getTitleTable(TABLE_COLUMNS, ['action'], []);
        const componentTable = {
            startTime: {
                key: 'startTime',
                render: (value, record, index) => this.renderTimeCell('startTime', value, index),
            },
            endTime: {
                key: 'endTime',
                render: (value, record, index) => this.renderTimeCell('endTime', value, index),
            },
            action: {
                key: 'action',
                width: 120,
                align: 'center',
                render: this.renderActionCell,
            },
        };

        return (
            <div>
                <div
                    className="d-flex justify-content-end align-items-center"
                    style={{ marginTop: '-36px', marginBottom: '8px' }}
                >
                    <Button type="primary" onClick={this.handleAddRow} disabled={disabled}>
                        Thêm khung giờ
                    </Button>
                </div>

                <HMS.ListComponent
                    tableKey="rate-operating-hours-table"
                    classChildren="p-0"
                    classParent="m-0"
                    rowKey={(record, index) => `${record.startTime || 'new'}-${record.endTime || 'row'}-${index}`}
                    dataSource={Func.getDataSource(this.state.operatingHours || [], TABLE_COLUMNS)}
                    columns={Func.columnsTable({
                        titleTable: titleItemTable,
                        component: componentTable,
                        itemName: 'rate',
                    })}
                    bordered
                    pagination={false}
                />
            </div>
        );
    }
}

export default OperatingHoursTable;
